import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { GridCustomCellProps } from "@progress/kendo-react-grid";
import { ChunkProgressBar } from "@progress/kendo-react-progressbars";

const formatMinutesToDHM = (totalMinutes: number) => {
  if (
    totalMinutes === undefined ||
    totalMinutes === null ||
    isNaN(totalMinutes)
  ) {
    return "0m";
  }

  // Convert inputs to precise absolute seconds
  const totalSeconds = Math.floor(totalMinutes * 60);

  // Calculate discrete time tokens
  const days = Math.floor(totalSeconds / (24 * 3600));
  const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  // Format into a padded, readable string
  const dDisplay = days > 0 ? String(days).padStart(1, "0") + "j " : "";
  const hDisplay = hours > 0 ? String(hours).padStart(1, "0") + "h " : "";
  const mDisplay = String(minutes).padStart(1, "0") + "m";

  return `Il y a ${dDisplay}${hDisplay}${mDisplay}`;
};

const getElapsedMinutes = (
  startDate: Date,
  endDate: Date = new Date(),
): number => {
  // Get time values in milliseconds
  const startMs = startDate.getTime();
  const endMs = endDate.getTime();

  // Calculate the difference
  const differenceMs = endMs - startMs;

  // Convert milliseconds to minutes (1000 ms * 60 seconds)
  const elapsedMinutes = differenceMs / 60000;

  // Choose the rounding strategy that fits your project needs:
  // Use Math.floor() for completed minutes, or Math.round() for the nearest minute
  return Math.max(Math.floor(elapsedMinutes), 1);
};

export const OperationEnCoursRenderer = (props: GridCustomCellProps) => {
  if (props.rowType === "groupHeader") {
    return null;
  }
  const progress =
    typeof props.dataItem.operEnAttente === "boolean"
      ? props.dataItem.operEnAttente
      : false;
  return (
    <td {...props.tdProps}>
      {progress ? (
        <div className="operation-in-progress">Opération en cours</div>
      ) : null}
    </td>
  );
};

export const OccupeRenderer = (props: GridCustomCellProps) => {
  if (props.rowType === "groupHeader") {
    return null;
  }
  const progress =
    typeof props.dataItem.tauxOccupation === "number"
      ? props.dataItem.tauxOccupation
      : 0;
  if (!props.dataItem.bins) {
    return <td {...props.tdProps}></td>;
  }
  return (
    <td {...props.tdProps}>
      <div style={{ width: "230px" }}>
        <ChunkProgressBar
          style={{
            width: "150px",
            height: "20px",
            marginRight: "20px",
            backgroundColor: "#ffffff",
          }}
          value={progress}
          chunkCount={props.dataItem.bins.length || 1}
          emptyStyle={{ backgroundColor: "#00466220" }}
          progressStyle={{ backgroundColor: "#004762" }}
        />
        {progress}%
      </div>
    </td>
  );
};

export const ModeRenderer = (props: GridCustomCellProps) => {
  if (props.rowType === "groupHeader") {
    return null;
  }
  return (
    <td {...props.tdProps}>
      <div
        className={`OM ${props.dataItem.modeOperation}`}
        id={`etiquettesection${props.dataItem.parcCd}`}
      >
        {props.dataItem.modeOperation === "MANUEL"
          ? "Manuel"
          : props.dataItem.modeOperation === "AUTOMATISATION"
            ? "Automatisé"
            : props.dataItem.modeOperation}
      </div>
    </td>
  );
};

export const ReservationRenderer = (props: GridCustomCellProps) => {
  if (props.rowType === "groupHeader") {
    return null;
  }
  return (
    <td {...props.tdProps}>
      <div className={`RSV ${props.dataItem.reservation}`}>
        {props.dataItem.reservation === "RCPT"
          ? "Réception"
          : props.dataItem.reservation === "EXPN"
            ? "Expédition"
            : props.dataItem.reservation}
      </div>
    </td>
  );
};

export const VerifierLeRenderer = (props: GridCustomCellProps) => {
  if (props.rowType === "groupHeader") {
    return null;
  }
  if (!props.dataItem.verifierLe) {
    return <td {...props.tdProps}></td>;
  }

  return (
    <td {...props.tdProps}>
      <CheckCircleIcon className="verifier" />
      {formatMinutesToDHM(
        getElapsedMinutes(new Date(props.dataItem.verifierLe)),
      )}{" "}
      par {props.dataItem.verifierParUsager}
    </td>
  );
};

export const ElapsedRenderer = (props: GridCustomCellProps) => {
  const max = 6 * 60; // 6 hours in minutes
  if (props.rowType === "groupHeader") {
    return null;
  }
  const dd = props.field ? props.field : "";
  const minutes = props.dataItem[dd]
    ? getElapsedMinutes(new Date(props.dataItem[dd]))
    : 0;
  if (minutes === 0) {
    return <td {...props.tdProps}></td>;
  }
  const percentage1 = Math.min((minutes / max) * 100, 100);
  const percentage2 = Math.max(percentage1 - 10, 0);
  const color =
    percentage1 > 75 ? "#8d1303" : percentage1 > 50 ? "#f7970d" : "#34ca06";
  const formattedTime = formatMinutesToDHM(minutes);
  return (
    <td {...props.tdProps}>
      <div>{formattedTime > "" ? `${formattedTime}` : ""}</div>
      <div
        style={{
          background: `linear-gradient(90deg, ${color} 0%, ${color} ${percentage2}%, rgba(255, 255, 255, 1) ${percentage1}%, rgba(255, 255, 255, 0) 100%)`,
          width: "100%",
          height: "5px",
          borderRadius: "1px",
        }}
      />
    </td>
  );
};
